import {
  collection,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '@/data/firebase';
import type { EventRow } from '@/lens';
import { isCasualPlay, mapEventDocs } from '@/lens';

/**
 * Firestore 에서 최근 회차의 이벤트를 가져온다 — 원본 커스텀 액션
 * `lensFetchRecentRunEvents` 의 짝.
 *
 * ★★★ **쿼리가 서버로 이관돼 있다 — 자르는 단위가 이벤트가 아니라 회차다.**
 * ⚠️ 이벤트로 자르면 창 경계가 회차 한가운데를 지나가, 그 회차의 `run_started`
 * 는 창 밖인데 `ending_reached` 는 안이 되어 **분모에서만 빠지고 완주율이
 * 부푼다.** 틀리는 방향이 한쪽으로 쏠려 표본을 늘려도 안 없어진다.
 *
 * ⚠️⚠️ **이 경로에서는 `lensSelectRecentRuns` 를 다시 걸지 않는다.**
 * 서버 쿼리가 이미 창과 쓰레기 필터를 씌웠다 — 원본 `lensLoadEvents` 도 그렇다.
 *
 * ⚠️ **복합 인덱스가 하나 필요하다** — `events`: `event_name` ASC +
 * `occurred_at` DESC. 없으면 **Firestore 모드에서만** 조회가 실패하고 목업은
 * 멀쩡해서 코드 문제로 보인다. 정의는 `firestore.indexes.json` 에 있고
 * 생성은 콘솔에서 한다(서비스 계정 키로는 권한이 없다).
 */

export interface FetchOptions {
  /** 남길 회차 수. 기본 100. */
  runLimit?: number;
  /** `run_started` 를 몇 건까지 훑을지. 기본 400. */
  scanLimit?: number;
  /** 이보다 턴이 적은 회차는 쓰레기로 본다. 기본 2. */
  minTurns?: number;
  /** 시딩 표본을 거르는 접두(쉼표 구분). */
  excludedRunPrefixes?: string;
}

/**
 * 가져온 결과 + **버린 것의 수**.
 *
 * ⚠️⚠️ 전에는 `EventRow[]` 만 돌려줬다 — 임의 플레이(`casual_play`) 회차를
 * **조용히 버렸다.** ★ 조용히 버리면 「그런 회차가 없었다」와 구분이 안 된다.
 * 이 프로젝트에서 반복해 나온 실패 유형이라 **센 값을 화면까지 올린다**
 * (개발 회신 39 §4 · 사용자 확정 2026-10-04).
 */
export interface FetchResult {
  rows: EventRow[];
  /** ★ 지인 시연 등으로 표시된 회차 수. ⚠️ 지표에는 안 들어간다. */
  casualRuns: number;
}

export async function fetchRecentRunEvents(opts: FetchOptions = {}): Promise<FetchResult> {
  const wanted = opts.runLimit ?? 100;
  const scan = opts.scanLimit ?? 400;
  const minT = opts.minTurns ?? 2;

  const excluded = (opts.excludedRunPrefixes ?? '')
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p !== '');
  // ⚠️ **후보 단계에서 거른다.** 2단계로 넘기면 이벤트를 받아온 뒤 버리게 되고
  //    그 낭비가 그대로 대기 시간이 된다.
  const isExcluded = (runId: string) => excluded.some((p) => runId.startsWith(p));

  const events = collection(db, 'events');

  // --- 1단계: 최근 `run_started` 를 훑어 후보 회차를 고른다 -------------------
  const startedSnap = await getDocs(
    query(
      events,
      where('event_name', '==', 'run_started'),
      orderBy('occurred_at', 'desc'),
      fsLimit(scan),
    ),
  );

  const candidates: string[] = [];
  const seen = new Set<string>();
  for (const doc of startedSnap.docs) {
    const runId = (doc.data()['run_id'] as string | undefined) ?? '';
    if (runId === '' || seen.has(runId)) continue;
    seen.add(runId);
    if (isExcluded(runId)) continue;
    candidates.push(runId);
  }

  // --- 2단계: 후보의 이벤트를 받아 쓸 만한 회차만 남긴다 ---------------------
  // ⚠️ `whereIn` 상한이 30 이라 끊어서 묻는다.
  const kept: string[] = [];
  const out: EventRow[] = [];
  // ★ 버린 수를 센다 — 조용히 버리지 않는다(개발 회신 39 §4).
  let casualRuns = 0;
  let i = 0;
  while (i < candidates.length && kept.length < wanted) {
    const chunk = candidates.slice(i, i + 30);
    i += chunk.length;

    const snap = await getDocs(query(events, where('run_id', 'in', chunk)));
    const byRun = new Map<string, Array<Record<string, unknown>>>();
    for (const doc of snap.docs) {
      const d = doc.data() as Record<string, unknown>;
      const runId = (d['run_id'] as string | undefined) ?? '';
      let list = byRun.get(runId);
      if (list === undefined) { list = []; byRun.set(runId, list); }
      list.push(d);
    }

    for (const runId of chunk) {
      if (kept.length >= wanted) break;
      const rows = byRun.get(runId);
      if (rows === undefined) continue;

      let turns = 0;
      let hasModel = false;
      let casual = false;
      for (const d of rows) {
        // ★ `casual_play` 는 **가져올 때만** 쓰는 거르개다 — struct 에 자리가 없다.
        if (isCasualPlay(d)) casual = true;
        const name = (d['event_name'] as string | undefined) ?? '';
        if (
          name === 'action_interpreted' ||
          name === 'unsupported_action_requested' ||
          name === 'input_blocked'
        ) {
          turns++;
        }
        if (((d['model_version'] as string | undefined) ?? '') !== '') hasModel = true;
      }
      // ⚠️ 쓰레기 회차 필터가 **창을 세는 조건 안에** 있다 — 나중에 붙이면
      //    창 크기를 다시 잡아야 한다.
      if (casual) {
        casualRuns += 1;
        continue;
      }
      if (turns < minT || !hasModel) continue;

      kept.push(runId);
      out.push(...mapEventDocs(rows));
    }
  }
  return { rows: out, casualRuns };
}
